<template>
  <master-layout id="profile-top" :show-footer="false" :enable-scroll-event="true">
    <div class="w-full min-h-full bg-[#F6F5F1]">
      <div
        v-if="loading"
        class="p-[16px] fixed top-0 start-0 w-full h-full bg-[#F6F5F1]/90 z-1000 flex items-center justify-center"
      >
        <Loading />
      </div>
      <template v-if="!showMobileOtp && !showEmailOtp">
        <BackButton class="px-5 md:px-6 lg:px-8" :customBack="handleBackButton" />
        <div class="w-full min-h-full pt-4 px-5 md:px-6 lg:px-8 pb-4">
          <h2
            class="text-[2rem] text-primary leading-10 font-inter font-black tracking-[-0.03em] mb-6"
          >
            Personal Information
          </h2>
          <div class="w-full flex bg-[#4844731F] p-[2px] rounded-[32px] mb-6">
            <button
              type="button"
              v-for="(item, index) in tabList"
              :key="index"
              @click="onTabClick(item.label)"
              class="flex-1 rounded-[28px] px-2 py-1.5 text-sm font-inter font-medium leading-5 text-secondary"
              :class="{
                'bg-white shadow-[0px 3px 1px 0px #0000000A]': currentTab === item.label,
              }"
            >
              {{ item.label }}
            </button>
          </div>
          <div class="w-full">
            <BioCard
              @number-verify="onVerifyNumber"
              @email-verify="onVerifyEmail"
              @hideFooter="hideFooter"
              :showError="showError"
              v-if="currentTab === 'Bio' && profileLoaded"
              :payload="payload"
              :originalPayload="originalPayload"
              scrollElementId="profile-top"
              :nationality-list="nationality"
              :nationality-loaded="nationalityLoaded"
              :schools-loaded="schoolsLoaded"
            />
            <ChurchLifeCard
              v-if="currentTab === 'Church Life'"
              :payload="payload"
              :cgData="cgData"
              @hideFooter="hideFooter"
            />
            <FamilyCard v-if="currentTab === 'Family'" :payload="payload" />
            <ToastSticky
              v-if="toastShow"
              :text="toastMsg"
              :type="toastStatus"
              @click="onCloseToastShow"
            />
          </div>
          <!-- The personal id this save would set belongs to another member: staff
               sort it out, so the member is offered support instead of a retry. -->
          <div
            v-if="supportNotice"
            class="w-full px-5 mt-6 flex flex-col gap-3"
            data-test="profile-support-notice"
          >
            <DsAlert variant="error" closable @close="supportNotice = ''">
              {{ supportNotice }}
            </DsAlert>
            <ContactSupportButton />
          </div>
          <!-- Save button always visible, but only enabled when there are changes -->
          <div
            class="w-full px-5 mt-6"
            :style="hasFocusedField ? `padding-bottom: ${keyboardInsetCss}` : ''"
          >
            <DsButton
              v-if="!checkShowError() && !hideSaveBtn"
              variant="secondary"
              shape="pill"
              size="56"
              class="w-full"
              :disabled="isSaving || justSaved || !canSave"
              :loading="isSaving"
              @click="save"
            >
              {{ isSaving ? 'Saving...' : 'Save Changes' }}
            </DsButton>
          </div>
        </div>
      </template>
      <!-- Confirmation Popup -->
      <div
        v-if="showPopup"
        class="fixed top-0 left-0 w-full h-full flex items-center p-[24px] z-[999] bg-[#5D5D5D99]"
        @wheel.prevent
        @scroll.prevent
      >
        <div class="w-full bg-paper rounded-[10px] p-[24px] flex flex-col gap-[24px]">
          <div class="flex gap-4 items-start justify-between">
            <h3 class="text-grey-700 text-2xl font-inter font-black tracking-[-0.03em]">
              Before you leave...
            </h3>
            <button type="button" @click="confirmLeave(false)" class="text-brown-800">
              <DsIcon name="close" size="md" />
            </button>
          </div>
          <p class="text-base font-inter font-medium text-grey-700">
            Save your changes first to make sure nothing is lost. 😀
          </p>
          <div class="w-full flex gap-[8px]">
            <DsButton variant="borderless" size="48" class="flex-1" @click="onLeavePage"
              >Leave page</DsButton
            >
            <DsButton
              variant="bordered"
              shape="pill"
              size="48"
              class="flex-1"
              @click="confirmLeave(false)"
              >Stay on page</DsButton
            >
          </div>
        </div>
      </div>
      <div v-else>
        <BackButton v-if="showEmailOtp || showMobileOtp" class="px-[20px]" :customBack="hideOtp" />
        <template v-if="showEmailOtp">
          <div class="register-otp">
            <otp
              is-otp-type="emailOtp"
              class="pt-[24px] font-[Inter]"
              :email="otpEmail"
              :is-onboarding="true"
              @nextButtonHandler="onEmailOtpVerified"
            ></otp>
          </div>
        </template>
        <template v-else-if="showMobileOtp">
          <div class="register-otp">
            <otp
              is-otp-type="mobileOtp"
              :mobile-number="otpMobileNumber"
              :country-code="otpCountryCode"
              class="pt-[24px] font-[Inter]"
              @nextButtonHandler="onMobileOtpVerified"
            ></otp>
          </div>
        </template>
      </div>
    </div>
  </master-layout>
</template>
<script>
import { mapGetters, mapState } from 'vuex';
import { isIosNativeOnly } from '@/utils/platform-check';
import Logout from '@/mixins/Logout.js';
import BioCard from './BioCard.vue';
import ChurchLifeCard from './ChurchLifeCard.vue';
import FamilyCard from './FamilyCard.vue';
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import Otp from '@/components/common/Otp.vue';
import axios from 'axios';
import { REGEX_MOBILE, REGEX_EMAIL } from '@/constants/validation/regex-patterns';
import { CG_VALIDATION_LIST } from '@/constants/registration-endpoints';
import { Capacitor } from '@capacitor/core';
import ToastSticky from '@/components/common/ToastSticky.vue';
import keyboardFieldIntoView from '@/mixins/keyboard-field-into-view';
import Loading from '@/components/common/LoadingState.vue';
import BackButton from '@/components/common/BackButton.vue';
import { triggerNotification } from '@/utils/haptics';
import { useFeatureFlags } from '@/composables/useFeatureFlags';
import {
  CAREER_FIELD_KEYS,
  PROFILE_CAREER_FIELDS_FLAG,
  careerFieldsPayload,
} from '@/constants/profile/careerFields';
import {
  Alert as DsAlert,
  Button as DsButton,
  Icon as DsIcon,
} from '@team-hogc/hogc-one-design-system';
import ContactSupportButton from '@/components/common/ContactSupportButton.vue';
import {
  PERSONAL_ID_LOCKED_SAVED,
  isPersonalIdClash,
  isPersonalIdLocked,
  personalIdClashMessage,
} from '@/utils/giving-login';
import { withLeadingPlus } from '@/utils/country-code';

export default {
  mixins: [keyboardFieldIntoView, Logout],
  components: {
    BioCard,
    ChurchLifeCard,
    FamilyCard,
    Otp,
    ToastSticky,
    Loading,
    BackButton,
    DsAlert,
    DsButton,
    DsIcon,
    ContactSupportButton,
  },
  data() {
    return {
      tabList: [{ label: 'Bio' }, { label: 'Church Life' }, { label: 'Family' }],
      currentTab: 'Bio',
      profileLoaded: false, // Track when profile data is loaded
      cgData: {},
      email: '',
      localMobileNumber: '',
      countryCode: '',
      cg: '',
      termsAndConditions: false,
      emailVerified: false,
      personalDetailsAccordian: false,
      POLAccordian: false,
      christianJourneyAccordian: false,
      toastMsg: 'Failed to save changes. Please try again.',
      // Set by a 409 PERSONAL_ID_CLASH: shown with a Contact support button.
      supportNotice: '',
      toastStatus: false,
      toastShow: false,
      payload: {
        familyName: '',
        givenName: '',
        preferredName: null,
        gender: '',
        dateOfBirth: '',
        partialNric: '',
        personalId: '',
        hasBioUpdateRequest: false,
        isMobileNumberVerified: 0,
        mobileNumber: '',
        email: '',
        stageOfLife: '',
        maritalStatus: '',
        haveChildren: true,
        numberOfChildren: null,
        cg: '',
        countryCode: '',
        nationalityId: null,
        levelId: null,
        schoolId: null,
        schoolName: '',
        otherPhaseName: null,
        industryId: null,
        otherIndustry: null,
        professionId: null,
        otherProfession: null,
        fieldOfStudyId: null,
        otherFieldOfStudy: null,
      },
      showError: {
        fullName: false,
        givenName: false,
        preferredName: false,
        dateOfBirth: false,
        partialNric: false,
        mobileNumber: false,
        email: false,
      },
      showMobileOtp: false,
      showEmailOtp: false,
      // Contact values the open OTP sheets belong to. Held separately from
      // payload.*, which is rebuilt from the server on every refresh.
      otpEmail: '',
      otpMobileNumber: '',
      otpCountryCode: '',
      hideSaveBtn: false,
      platform: Capacitor.getPlatform(),
      nationality: [],
      nationalityLoaded: false,
      schoolsLoaded: false,
      isSaving: false,
      justSaved: false,
      baselinePayload: null,
      isButtonBack: false,
    };
  },
  setup() {
    const router = useRouter();
    const showPopup = ref(false);
    const allowContinue = ref(false);
    const nextRoute = ref(null); // Ensure it's reactive

    const confirmLeave = confirm => {
      if (confirm) {
        showPopup.value = false;
        if (nextRoute.value) {
          allowContinue.value = true;
          router.push(nextRoute.value); // Navigate
        }
      } else {
        showPopup.value = false; // Close popup
      }
    };
    return { showPopup, nextRoute, confirmLeave, allowContinue };
  },
  mounted() {
    this.getCgData();
    this.startKeyboardOverlapTracking();
  },
  beforeRouteLeave(to, from, next) {
    if (this.hasChanges() && this.isButtonBack) {
      // Back button tap with unsaved changes — show modal + haptic
      this.isButtonBack = false;
      triggerNotification('warning');
      this.nextRoute = to.fullPath;
      this.showPopup = true;

      if (!this.allowContinue) {
        next(false);
      } else {
        this.showPopup = false;
        this.allowContinue = false;
        next();
      }
    } else {
      // Swipe-back or no changes — just leave
      this.isButtonBack = false;
      next();
    }
  },
  computed: {
    ...mapState('profile', {
      loading: state => state.loading,
      originalEmailVerified: state => state.isEmailVerified,
    }),
    ...mapState('lookups', [
      'genders',
      'nationalities',
      'residentialAreas',
      'maritalStatuses',
      'educationalPhases',
      'industries',
      'schools',
      'course',
      'transitions',
      'stagesOfLife',
      'christianJourneys',
      'years',
      'cgMapping',
    ]),
    ...mapState('registration', ['isLoading']),
    ...mapGetters({
      originalEmail: 'profile/email',
      originalPayload: 'profile/payload',
      originalMobile: 'profile/localMobileNumber',
      originalCountryCode: 'profile/countryCode',
      cgName: 'profile/cgName',
      fullName: 'profile/fullName',
    }),

    careerFieldsEnabled() {
      const { isEnabled } = useFeatureFlags();
      return isEnabled(PROFILE_CAREER_FIELDS_FLAG);
    },
    difference() {
      const diff = {};
      // Explicitly list fields that should trigger "Save Changes" button
      // Matches the fields sent to API in save() method, excluding fields with separate OTP verification flows
      const includedFields = [
        'fullName',
        'familyName',
        'preferredName',
        'gender',
        'dateOfBirth',
        'partialNric',
        'stageOfLife',
        'maritalStatus',
        'haveChildren',
        'numberOfChildren',
        'cg',
        'nationalityId',
        'levelId',
        'schoolId',
        'phaseId',
        'otherPhaseName',
        ...(this.careerFieldsEnabled ? CAREER_FIELD_KEYS : []),
      ];

      // Only check included fields for changes
      // Compare against baselinePayload (post-transformation snapshot) to avoid false positives
      // from field transformations in refresh() (cg lookup, countryCode default, schoolId override)
      const baseline = this.baselinePayload || this.originalPayload;
      for (const key of includedFields) {
        const payloadValue = this.payload[key];
        const originalValue = baseline[key];

        // Normalize null/undefined for otherPhaseName
        let normalizedPayloadValue = payloadValue;
        let normalizedOriginalValue = originalValue;
        if (key === 'otherPhaseName') {
          normalizedPayloadValue = payloadValue === undefined ? null : payloadValue;
          normalizedOriginalValue = originalValue === undefined ? null : originalValue;
        }

        // Check if values are different using strict equality
        // This handles all cases including false, 0, null, undefined, empty string
        if (normalizedPayloadValue !== normalizedOriginalValue) {
          // Include the change if either value exists (not null/undefined) or if both are explicitly set
          // This ensures we detect changes from null/undefined to a value, or from a value to null/undefined
          // Also handles boolean and number changes correctly
          if (
            (normalizedPayloadValue !== null && normalizedPayloadValue !== undefined) ||
            (normalizedOriginalValue !== null && normalizedOriginalValue !== undefined)
          ) {
            diff[key] = payloadValue;
          }
        }
      }
      return diff;
    },
    isValidOtherPhaseName() {
      // If stageOfLife is not 'OTHERS', validation passes
      if (this.payload.stageOfLife !== 'OTHERS') {
        return true;
      }
      // If 'OTHERS' is selected, otherPhaseName must be non-empty
      const otherPhaseName = this.payload.otherPhaseName;
      return otherPhaseName && typeof otherPhaseName === 'string' && otherPhaseName.trim() !== '';
    },
    canSave() {
      return this.hasChanges() && this.isValidOtherPhaseName;
    },
  },
  watch: {
    difference(newVal) {
      // Reset justSaved when new changes are made
      if (Object.keys(newVal).length > 0 && this.justSaved) {
        this.justSaved = false;
      }
    },
  },
  methods: {
    async getCgData() {
      try {
        const { status, data } = await axios.get(process.env.VUE_APP_API_URL + CG_VALIDATION_LIST);
        if (status !== 200) throw new Error('Failed in getting CG data');
        this.cgData = data;
      } catch (e) {
        console.log('error', e);
      }
    },
    hideFooter(val) {
      this.hideSaveBtn = val;
    },
    isIos() {
      return isIosNativeOnly();
    },
    onTabClick(label) {
      this.currentTab = label;
    },
    hasChanges() {
      return Object.keys(this.difference).length > 0;
    },
    checkShowError() {
      for (const key of Object.keys(this.showError)) {
        if (this.showError[key] === true) {
          return true;
        }
      }
      return false;
    },
    isValidMobileNumber(value) {
      if (!value) return false; // Prevents errors on empty values
      return REGEX_MOBILE.test(value);
    },
    isValidEmail(value) {
      return REGEX_EMAIL.test(value);
    },
    checkValidField() {
      if (this.payload.fullName === '') {
        this.showError.fullName = true;
        return false;
      }
      this.showError.fullName = false;

      if (this.payload.dateOfBirth === '') {
        this.showError.dateOfBirth = true;
        return false;
      }
      this.showError.dateOfBirth = false;

      const isPartialNricEmpty =
        !this.payload.partialNric || this.payload.partialNric.trim() === '';
      // A locked personal id is not this login's to fill in (giving per login).
      if (
        !this.payload.personalIdLocked &&
        this.payload.hasBioUpdateRequest === true &&
        isPartialNricEmpty
      ) {
        this.showError.partialNric = true;
        return false;
      }
      this.showError.partialNric = false;

      if (this.payload.isMobileNumberVerified !== 0 && this.payload.mobileNumber === '') {
        this.showError.mobileNumber = true;
        return false;
      }
      this.showError.mobileNumber = false;

      if (this.payload.email === '') {
        this.showError.email = true;
        return false;
      }
      this.showError.email = false;

      return true;
    },
    async save() {
      if (this.isSaving) return;

      if (this.checkValidField() === false) {
        this.toastMsg = 'Failed to save changes. Please try again.';
        this.toastStatus = false;
        this.toastShow = true;
        setTimeout(() => {
          this.toastShow = false;
        }, 3000);
        return;
      }

      if (this.hasChanges()) {
        this.isSaving = true;
        this.supportNotice = '';

        try {
          // Explicitly construct payload with only source fields needed by API
          const profileData = {
            fullName: this.payload.fullName,
            familyName: this.payload.familyName,
            preferredName: this.payload.preferredName ?? null,
            gender: this.payload.gender,
            dateOfBirth: this.payload.dateOfBirth,
            mobileNumber: this.payload.mobileNumber,
            email: this.payload.email,
            stageOfLife: this.payload.stageOfLife,
            maritalStatus: this.payload.maritalStatus,
            haveChildren: this.payload.haveChildren,
            cg: this.payload.cg,
            nationalityId: this.payload.nationalityId,
            levelId: this.payload.levelId,
            schoolId: this.payload.schoolId,
            phaseId: this.payload.phaseId,
          };

          if (this.careerFieldsEnabled) {
            Object.assign(profileData, careerFieldsPayload(this.payload));
          }

          // Add countryCode with one '+' prefix if present; the stored code may already carry it.
          if (this.payload.countryCode) {
            profileData.countryCode = withLeadingPlus(this.payload.countryCode);
          }

          // Add partialNric only if it has a valid value (API validation requires it to be omitted or have valid value)
          // Never for a locked personal id: the API would refuse it.
          if (
            !this.payload.personalIdLocked &&
            this.payload.partialNric &&
            this.payload.partialNric !== '' &&
            this.payload.partialNric !== null
          ) {
            profileData.partialNric = this.payload.partialNric;
          }

          // Add numberOfChildren only if haveChildren is true
          if (
            this.payload.haveChildren === true &&
            this.payload.numberOfChildren !== null &&
            this.payload.numberOfChildren !== undefined
          ) {
            profileData.numberOfChildren = this.payload.numberOfChildren;
          }

          // Add otherPhaseName only if stageOfLife is 'OTHERS'
          if (this.payload.stageOfLife === 'OTHERS') {
            profileData.otherPhaseName = this.payload.otherPhaseName?.trim();
          }

          // Handle "Others" school case - when schoolId = 0, send school name instead of schoolId
          if (this.payload.schoolId == 0 && this.payload.otherSchool?.trim()) {
            profileData.schoolId = undefined;
            profileData.schoolName = this.payload.otherSchool.trim();
          }

          const verified = await this.$store.dispatch('profile/updateProfile', profileData);

          // Treat a missing flag as unverified — a strict `=== false` check silently
          // skipped the OTP prompt whenever the API omitted the key.
          const emailNeedsOtp = verified && !verified.isEmailVerified;
          const mobileNeedsOtp = verified && !verified.isMobileNumberVerified;

          if (emailNeedsOtp || mobileNeedsOtp) {
            if (emailNeedsOtp) {
              // Pin the submitted address — payload.email is replaced by the
              // forceFetchProfile below before the user can enter the code.
              this.otpEmail = profileData.email;
              this.showEmailOtp = true;
            } else if (mobileNeedsOtp) {
              const isMobileNumberOptional = this.payload.isMobileNumberVerified === 0;
              const hasMobileNumber =
                this.payload.mobileNumber && this.payload.mobileNumber.trim() !== '';

              if (!isMobileNumberOptional || hasMobileNumber) {
                // Pin the submitted number — payload.mobileNumber is replaced by
                // the forceFetchProfile below before the user can enter the code.
                this.otpMobileNumber = profileData.mobileNumber;
                // profileData.countryCode is only set when payload.countryCode is
                // truthy; fall back to what the template used to compute.
                this.otpCountryCode =
                  profileData.countryCode || withLeadingPlus(this.payload.countryCode);
                this.showMobileOtp = true;
              }
            }
          }

          if (!verified) {
            this.toastMsg = 'Failed to save changes. Please try again.';
            this.toastStatus = false;
            this.toastShow = true;
            setTimeout(() => {
              this.toastShow = false;
            }, 3000);
            return;
          }

          await this.reloadAfterSave();

          this.justSaved = true;
          this.toastMsg = 'Changes saved successfully!';
          this.toastStatus = true;
          this.toastShow = true;
          setTimeout(() => {
            this.toastShow = false;
          }, 3000);
        } catch (error) {
          if (isPersonalIdLocked(error)) {
            // Everything but the personal id was written (contract section 8).
            await this.reloadAfterSave().catch(() => {});
            this.justSaved = true;
            this.showToast(PERSONAL_ID_LOCKED_SAVED, true);
          } else if (isPersonalIdClash(error)) {
            // The API writes the rest of this save before it answers 409, so the
            // form shows what was kept; the notice stays up with Contact support.
            this.supportNotice = personalIdClashMessage(error);
            await this.reloadAfterSave().catch(() => {});
          } else {
            const message = Array.isArray(error.response?.data?.message)
              ? error.response.data.message.join(', ')
              : error.response?.data?.message;
            this.showToast(message || 'Failed to save changes. Please try again.', false);
          }
        } finally {
          this.isSaving = false;
        }
      }
    },
    showToast(message, success) {
      this.toastMsg = message;
      this.toastStatus = success;
      this.toastShow = true;
      setTimeout(() => {
        this.toastShow = false;
      }, 3000);
    },
    async reloadAfterSave() {
      await this.$store.dispatch('profile/forceFetchProfile');
      await this.$nextTick();

      this.payload = { ...this.originalPayload };
      this.payload.cg = Object.keys(this.cgData).find(key => this.cgData[key] === this.payload.cg);
      if (!this.payload.countryCode) {
        this.payload.countryCode = '65';
      }

      // Handle "Others" school case - if schoolName exists in store, set schoolId to 0
      if (this.$store.state.profile?.schoolName) {
        this.payload.schoolId = 0;
        this.payload.otherSchool = this.$store.state.profile.schoolName;
      }

      // Snapshot the transformed payload as the baseline for change detection
      this.baselinePayload = { ...this.payload };
    },
    onCloseToastShow() {
      this.toastShow = false;
    },
    onVerifyNumber() {
      // Pin the number the OTP is for, same reason as otpEmail.
      if (!this.showMobileOtp) {
        this.otpMobileNumber = this.payload.mobileNumber;
        this.otpCountryCode = withLeadingPlus(this.payload.countryCode);
      }
      this.showMobileOtp = !this.showMobileOtp;
    },
    async onMobileOtpVerified() {
      this.showMobileOtp = false;
      this.otpMobileNumber = '';
      this.otpCountryCode = '';
      // Pull the newly verified number back into the form.
      await this.refresh();
    },
    onVerifyEmail() {
      // Pin the address the OTP is for. payload.email is rebuilt from the server
      // after every save/refresh, so binding the sheet to it meant the code was
      // sent to one address and then submitted for another (or for '').
      if (!this.showEmailOtp) this.otpEmail = this.payload.email;
      this.showEmailOtp = !this.showEmailOtp;
    },
    async onEmailOtpVerified() {
      this.showEmailOtp = false;
      this.otpEmail = '';
      // Pull the newly verified address back into the form.
      await this.refresh();
    },
    handleBackButton() {
      this.isButtonBack = true;
      this.$router.push('/mepage');
    },
    onLeavePage() {
      this.showMobileOtp = false;
      this.showEmailOtp = false;
      this.confirmLeave(true);
    },
    hideOtp() {
      this.showMobileOtp = false;
      this.showEmailOtp = false;
      this.refresh();
    },
    async refresh() {
      await Promise.allSettled([
        this.$store.dispatch('profile/getSchools'),
        this.$store.dispatch('registration/fetchNationality'),
        this.$store.dispatch('profile/forceFetchProfile'),
      ]);

      this.payload = { ...this.originalPayload };
      this.payload.cg = Object.keys(this.cgData).find(key => this.cgData[key] === this.payload.cg);
      if (!this.payload.countryCode) {
        this.payload.countryCode = '65';
      }

      // Handle "Others" school case - if schoolName exists in store, set schoolId to 0
      if (this.$store.state.profile?.schoolName) {
        this.payload.schoolId = 0;
        this.payload.otherSchool = this.$store.state.profile.schoolName;
      }

      // Snapshot the transformed payload as the baseline for change detection
      this.baselinePayload = { ...this.payload };

      this.schoolsLoaded = true;
      this.profileLoaded = true;

      this.localMobileNumber = this.originalMobile;
      this.countryCode = this.originalCountryCode;
      this.email = this.originalEmail;
      this.emailVerified = this.originalEmailVerified;
      this.cg = this.cgName;
    },
  },
  async ionViewWillEnter() {
    this.$store.commit('profile/setLoading', true);
    this.currentTab = this.tabList[0].label;
    // On a direct load of /profile, Ionic runs this hook before the page is in the
    // document, so the lookup can be null. A fresh page is already at the top, so
    // skip the scroll rather than throw and leave nationality unloaded below.
    const elem = document.getElementById('profile-top');
    await this.refresh();
    await elem?.scrollToTop();
    this.nationality = this.$store.getters['registration/nationalityForSelect'];
    this.nationalityLoaded = true;
  },
};
</script>
